import {
  AddBox,
  Delete,
  Edit,
  Help,
  History,
  PictureAsPdf,
  TableView,
  LocalShipping,
  Visibility,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FirstPage,
  LastPage,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import Paper from "@mui/material/Paper";
import {
  GridColDef,
  GridRenderEditCellParams,
  GridRowSelectionModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { updatePaquete, type Paquete, type PaqueteDto } from "../../firebase/firestore/paquetes";
import {
  validateCreditsAgency,
  validateHasRates,
} from "../../utils/agencyValidations";
import {
  camposFaltantesSegunEstado,
  mensajeEntregaIncompleta,
} from "../../utils/entregaValidations";
import { exportExcel, exportPDF } from "../../utils/exportTable";
import { useAgenciaStore } from "../../zustand/useAgenciaStore";
import { useAuthStore } from "../../zustand/useAuthStore";
import { useDateRangeStore } from "../../zustand/useDateRangeStore";
import { usePaqueteStore } from "../../zustand/usePaquetesStore";
import { UserRole } from "../../interfaces/UserRole";
import { useEstados, formatEstadoNombre } from "../../utils/estadosUtils";
import ModalDeletePackage from "../modals/ModalDeletePackage";
import ModalHasNotRates from "../modals/ModalHasNotRates";
import ModalNoCredit from "../modals/ModalNoCredit";
import { STEP_RESUMEN } from "../packages/FormPackage";
import ModalFormPackage from "../modals/ModalPackage";
import ModalPackagesInBatch from "../modals/ModalPackagesInBatch";
import ModalCreateShipment from "../modals/ModalCreateShipment";
import ModalTimeline from "../modals/ModalTimeline";
import type { TimelineItem } from "../timeline/Timeline";
import DataTable from "./DataTable";
import FooterTable from "./FooterTable";

// Set Spanish locale
dayjs.locale("es");

// Custom edit component for estado that auto-opens the Select dropdown
const EditEstadoCell = ({ estados, ...params }: GridRenderEditCellParams & { estados: Array<{ id: string; nombre: string }> }) => {
  const [open, setOpen] = useState(true); // start opened
  // Evita salir dos veces del modo edicion: onClose tambien se dispara al seleccionar
  const commitedRef = useRef(false);

  const handleChange = (e: { target: { value: unknown } }) => {
    commitedRef.current = true;
    params.api.setEditCellValue({
      id: params.id,
      field: params.field,
      value: e.target.value,
    });
    // Close and commit immediately
    setOpen(false);
    params.api.stopCellEditMode({ id: params.id, field: params.field });
  };

  // El backdrop del Select se queda con el click de afuera, asi que hay que
  // salir del modo edicion aqui o la celda queda con el dropdown cerrado
  const handleClose = () => {
    setOpen(false);
    if (commitedRef.current) return;
    params.api.stopCellEditMode({
      id: params.id,
      field: params.field,
      ignoreModifications: true,
    });
  };

  return (
    <Select
      value={params.value}
      onChange={handleChange}
      fullWidth
      size="small"
      autoFocus
      open={open}
      onClose={handleClose}
      onOpen={() => setOpen(true)}
      MenuProps={{
        // Prevent scroll jump
        disableAutoFocusItem: true,
      }}
    >
      {estados.map((estado) => (
        <MenuItem key={estado.id} value={estado.id}>
          {estado.nombre}
        </MenuItem>
      ))}
    </Select>
  );
};

interface TablePackagesProps {
  statusFilter?: string | null;
  onClearFilter?: () => void;
  envioFilter?: string | null;
  onClearEnvioFilter?: () => void;
}

export default function TablePackages({
  statusFilter = null,
  onClearFilter,
  envioFilter = null,
  onClearEnvioFilter,
}: TablePackagesProps) {
  const { fechaInicio, fechaFin } = useDateRangeStore();
  const { agencia } = useAgenciaStore();
  const { user } = useAuthStore();
  const estados = useEstados();
  const apiRef = useGridApiRef();
  const canDelete = user?.role === UserRole.Admin;

  // Function to format date in a friendly way
  const formatFriendlyDate = (fecha: any, hora: any) => {
    try {
      let dateObj: dayjs.Dayjs;

      // Handle different date formats
      if (typeof fecha === "string" && typeof hora === "string") {
        // If fecha and hora are strings, combine them
        const dateTimeStr = `${fecha} ${hora}`;
        dateObj = dayjs(dateTimeStr);
      } else if (fecha && hora) {
        // If they are Timestamps or other formats
        dateObj = dayjs(fecha);
      } else {
        return "-";
      }

      if (!dateObj.isValid()) {
        return "-";
      }

      // Format: "12 de Octubre a las 2:30pm"
      const day = dateObj.format("D");
      const month = dateObj.format("MMMM");
      const time = dateObj.format("h:mmA").toLowerCase();

      return `${day} de ${month} a las ${time}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "-";
    }
  };
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenModalNoCredit, setIsOpenModalNoCredit] = useState(false);
  const [isOpenModalDeletePackage, setIsOpenModalDeletePackage] =
    useState(false);
  const [isOpenModalHasNotRates, setIsOpenModalHasNotRates] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [isOpenModalInBatch, setIsOpenModalInBatch] = useState(false);
  const [isOpenModalCreateShipment, setIsOpenModalCreateShipment] = useState(false);
  const [isOpenModalTimeline, setIsOpenModalTimeline] = useState(false);
  const [search, setSearch] = useState("");
  const [entity, setEntity] = useState<PaqueteDto>();
  const [initialStep, setInitialStep] = useState(0);
  const [packageForDelete, setPackageForDelete] = useState<PaqueteDto>();
  const [packageForTimeline, setPackageForTimeline] = useState<PaqueteDto>();
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>();
  const { 
    countTotal, 
    allPaquetes, 
    resetPackages, 
    fetchCounts,
    fetchAllPaquetes,
    isLoadingAll,
    updatePaqueteInStore,
  } = usePaqueteStore();

  useEffect(() => {
    resetPackages();
    fetchAllPaquetes();
  }, [resetPackages, fetchAllPaquetes]);

  const handleIsOpenModal = (b?: boolean) => setIsOpen(b ?? !isOpen);

  const filteredRows = allPaquetes.filter((row) => {
    // Filtrar por estado si hay un filtro activo
    if (statusFilter && row.estado !== statusFilter) {
      return false;
    }

    // Filtrar por envío si hay un filtro activo
    if (envioFilter && row.envio !== envioFilter) {
      return false;
    }

    if (!search.trim()) return true;

    const searchLower = search.toLowerCase();

    // Search in idRastreo
    if (row.idRastreo?.toLowerCase().includes(searchLower)) return true;

    // Search in contenido
    if (row.contenido?.toLowerCase().includes(searchLower)) return true;

    // Search in ubicacionFisica
    if (row.ubicacionFisica?.toLowerCase().includes(searchLower)) return true;

    // Search in usuario (name, lastName, lockerCode)
    if (row.usuario) {
      const usuarioStr = `${row.usuario.name || ""} ${
        row.usuario.lastName || ""
      } ${row.usuario.lockerCode || ""}`.toLowerCase();
      if (usuarioStr.includes(searchLower)) return true;
    }

    // Search in via
    if (row.via?.toLowerCase().includes(searchLower)) return true;

    // Search in peso (monto and unidad)
    if (row.peso) {
      const pesoStr = `${row.peso.monto || ""} ${
        row.peso.unidad || ""
      }`.toLowerCase();
      if (pesoStr.includes(searchLower)) return true;
    }

    // Search in tarifa (nombre, monto, moneda)
    if (row.tarifa) {
      const tarifaStr = `${row.tarifa.nombre || ""} ${row.tarifa.monto || ""} ${
        row.tarifa.moneda || ""
      }`.toLowerCase();
      if (tarifaStr.includes(searchLower)) return true;
    }

    // Search in cobroAdicional
    if (
      row.cobroAdicional !== undefined &&
      row.cobroAdicional !== null
    ) {
      if (String(row.cobroAdicional).includes(searchLower)) return true;
    }

    // Search in total
    if (row.total !== undefined && row.total !== null) {
      if (String(row.total).includes(searchLower)) return true;
    }

    // Search in costo
    if (row.costo !== undefined && row.costo !== null) {
      if (String(row.costo).includes(searchLower)) return true;
    }

    // Search in descuento
    if (row.descuento !== undefined && row.descuento !== null) {
      if (String(row.descuento).includes(searchLower)) return true;
    }

    // Search in ganancia
    if (row.ganancia !== undefined && row.ganancia !== null) {
      if (String(row.ganancia).includes(searchLower)) return true;
    }

    // Search in estado
    if (row.estado?.toLowerCase().includes(searchLower)) return true;

    // Search in envio
    if (row.envio?.toLowerCase().includes(searchLower)) return true;

    // Search in rastreo (formatted date)
    if (row.rastreo && Array.isArray(row.rastreo) && row.rastreo.length > 0) {
      const lastRastreo = row.rastreo[row.rastreo.length - 1];
      if (lastRastreo?.fecha && lastRastreo?.hora) {
        const rastreoDateStr = formatFriendlyDate(
          lastRastreo.fecha,
          lastRastreo.hora
        ).toLowerCase();
        if (rastreoDateStr.includes(searchLower)) return true;
      }
    }

    return false;
  });

  const openPackageModal = (paquete?: PaqueteDto, step = 0) => {
    setEntity(paquete);
    setInitialStep(step);
    setIsOpen(true);
  };

  // Editar abre el wizard desde el primer paso.
  const handleEdit = (paquete?: PaqueteDto) => openPackageModal(paquete, 0);

  // Ver abre el wizard directamente en el resumen del paquete.
  const handleView = (paquete?: PaqueteDto) =>
    openPackageModal(paquete, STEP_RESUMEN);

  const handleCloseModal = () => {
    handleIsOpenModal(false);
    setEntity(undefined);
    setInitialStep(0);
  };

  // Convertir rastreo de paquete a TimelineItem[]
  const getTimelineItemsFromPaquete = (paquete: PaqueteDto): TimelineItem[] => {
    if (!paquete.rastreo || !Array.isArray(paquete.rastreo) || paquete.rastreo.length === 0) {
      return [];
    }
    return paquete.rastreo.map((item) => ({
      estado: item.estado as TimelineItem["estado"],
      fecha: item.fecha,
      hora: item.hora,
    }));
  };

  const handleCreate = async () => {
    // Validate credits
    if (await validateCreditsAgency(agencia)) {
      // Validate has rates
      if (validateHasRates(agencia)) {
        openPackageModal(undefined, 0);
      } else {
        // Modal hasn't rates
        setIsOpenModalHasNotRates(true);
      }
    } else {
      // Modal contact soport
      setIsOpenModalNoCredit(true);
    }
  };

  const handleDeletePackages = (pack?: PaqueteDto) => {
    setPackageForDelete(pack);
    setIsOpenModalDeletePackage(true);
  };

  const columns: GridColDef[] = [
    {
      field: "acciones",
      headerName: "Acciones",
      width: 200,
      sortable: false, // Actions column should not be sortable
      renderCell: (params) => (
        <Box
          sx={{ display: "flex", gap: 1, alignItems: "center", height: "100%" }}
        >
          <Tooltip title="Ver">
            <Button
              variant="outlined"
              sx={{ minWidth: "auto", padding: "5px" }}
              onClick={() => handleView(params.row as PaqueteDto)}
            >
              <Visibility sx={{ fontSize: 20 }} />
            </Button>
          </Tooltip>
          <Tooltip title="Ver historial">
            <Button
              variant="outlined"
              sx={{ minWidth: "auto", padding: "5px" }}
              onClick={() => {
                setPackageForTimeline(params.row as PaqueteDto);
                setIsOpenModalTimeline(true);
              }}
            >
              <History sx={{ fontSize: 20 }} />
            </Button>
          </Tooltip>
          <Tooltip title="Editar">
            <Button
              variant="outlined"
              sx={{ minWidth: "auto", padding: "5px" }}
              onClick={() => handleEdit(params.row)}
            >
              <Edit sx={{ fontSize: 20 }} />
            </Button>
          </Tooltip>
          {canDelete && (
            <Tooltip title="Eliminar">
              <Button
                variant="outlined"
                color="error"
                sx={{ minWidth: "auto", padding: "5px" }}
                onClick={() => handleDeletePackages(params.row as PaqueteDto)}
              >
                <Delete sx={{ fontSize: 20 }} />
              </Button>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "idRastreo",
      headerName: "ID",
      width: 160,
      sortable: true,
    },
    {
      field: "usuario",
      headerName: "Usuario",
      width: 250,
      sortable: true,
      valueGetter: (value, row) => {
        const user = row.usuario;
        if (!user) return "";
        return `${user.name || ""} ${user.lastName || ""} ${
          user.lockerCode || ""
        }`
          .trim()
          .toLowerCase();
      },
      sortComparator: (v1, v2) => {
        return v1.localeCompare(v2);
      },
      renderCell: (params) => {
        const user = params.row.usuario;
        if (!user) return "-";
        return `${user.name} ${user.lastName} - ${user.lockerCode}`;
      },
    },
    {
      field: "via",
      headerName: "Vía",
      width: 80,
      sortable: true,
    },
    {
      field: "estado",
      renderHeader() {
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            Estado
            <Tooltip title="Presione una vez en el estado para modificarlo">
              <Help fontSize="small" />
            </Tooltip>
          </Box>
        );
      },
      headerName: "Estado",
      width: 180,
      editable: true,
      sortable: true,
      renderCell: (params) => {
        const estadoId = params.value as string;
        const estado = estados.find((e) => e.id === estadoId);
        const estadoNombre = estado?.nombre || formatEstadoNombre(estadoId);
        
        // Obtener colores según el tema actual (usar light por defecto para simplificar)
        const colors = estado 
          ? [estado.colores.light.aereo.a, estado.colores.light.aereo.b]
          : ["#939c9fab", "#4a4a4a"];
        
        return (
          <Chip
            label={estadoNombre}
            sx={{
              backgroundColor: colors[0],
              color: "#fff",
              textTransform: "capitalize",
              fontWeight: "bold",
              cursor: "pointer",
            }}
            onClick={() => {
              if (!apiRef.current) return;
              apiRef.current.startCellEditMode({
                id: params.id,
                field: params.field,
              });
            }}
          />
        );
      },
      renderEditCell: (params: GridRenderEditCellParams) => {
        return <EditEstadoCell {...params} estados={estados} />;
      },
    },
    {
      field: "total",
      headerName: "Total",
      width: 120,
      sortable: true,
      valueGetter: (value, row) => {
        const total = row.total;
        if (total === undefined || total === null) {
          return 0;
        }
        return total;
      },
      sortComparator: (v1, v2) => {
        if (v1 === -1 && v2 === -1) return 0;
        if (v1 === -1) return 1;
        if (v2 === -1) return -1;
        return v1 - v2;
      },
    },
    {
      field: "peso",
      headerName: "Peso",
      width: 100,
      sortable: true,
      valueGetter: (value, row) => {
        const peso = row.peso;
        if (!peso || peso.monto === undefined || peso.monto === null) {
          return -1; // Put null/undefined values at the end
        }
        return peso.monto;
      },
      sortComparator: (v1, v2) => {
        if (v1 === -1 && v2 === -1) return 0;
        if (v1 === -1) return 1;
        if (v2 === -1) return -1;
        return v1 - v2;
      },
      renderCell: (params) => {
        const peso = params.row.peso;
        if (
          !peso ||
          peso.monto === undefined ||
          peso.monto === null ||
          !peso.unidad
        ) {
          return "-";
        }
        return `${peso.monto} ${peso.unidad}`;
      },
    },
    {
      field: "ubicacionFisica",
      headerName: "Ubicación física",
      width: 180,
      sortable: true,
      renderCell: (params) => {
        const ubicacionFisica = params.value as string | undefined;
        return ubicacionFisica || "-";
      },
    },
    {
      field: "envio",
      headerName: "Envío",
      width: 200,
      sortable: true,
      renderCell: (params) => {
        const envio = params.value as string | undefined;
        return envio || "-";
      },
    },
    {
      field: "tarifa",
      headerName: "Tarifa",
      width: 120,
      sortable: true,
      valueGetter: (value, row) => {
        const tarifa = row.tarifa;
        if (!tarifa || tarifa.monto === undefined || tarifa.monto === null) {
          return -1; // Put null/undefined values at the end
        }
        return tarifa.monto;
      },
      sortComparator: (v1, v2) => {
        if (v1 === -1 && v2 === -1) return 0;
        if (v1 === -1) return 1;
        if (v2 === -1) return -1;
        return v1 - v2;
      },
      renderCell: (params) => {
        const tarifa = params.row.tarifa;
        if (
          !tarifa ||
          !tarifa.nombre ||
          tarifa.monto === undefined ||
          tarifa.monto === null ||
          !tarifa.moneda
        ) {
          return "-";
        }
        return `${tarifa.nombre} - ${tarifa.monto} ${tarifa.moneda}`;
      },
    },
    {
      field: "contenido",
      headerName: "Contenido",
      width: 200,
      sortable: true,
      renderCell: (params) => {
        const contenido = params.value;
        if (!contenido) {
          return "-";
        }
        return contenido;
      },
    },
    {
      field: "cobroAdicional",
      headerName: "Cobro Adicional",
      width: 120,
      sortable: true,
      valueGetter: (value, row) => {
        const cobro = row.cobroAdicional;
        if (cobro === undefined || cobro === null) {
          return 0;
        }
        return cobro;
      },
      renderCell: (params) => {
        const cobro = params.value;
        if (cobro === undefined || cobro === null) {
          return "0";
        }
        return cobro;
      },
    },
    {
      field: "descuento",
      headerName: "Descuento",
      width: 120,
      sortable: true,
      valueGetter: (value, row) => {
        const descuento = row.descuento;
        if (descuento === undefined || descuento === null) {
          return 0;
        }
        return descuento;
      },
      sortComparator: (v1, v2) => {
        if (v1 === -1 && v2 === -1) return 0;
        if (v1 === -1) return 1;
        if (v2 === -1) return -1;
        return v1 - v2;
      },
      renderCell: (params) => {
        const descuento = params.value;
        if (descuento === undefined || descuento === null) {
          return "-";
        }
        return descuento;
      },
    },
    {
      field: "costo",
      headerName: "Costo",
      width: 120,
      sortable: true,
      valueGetter: (value, row) => {
        const costo = row.costo;
        if (costo === undefined || costo === null) {
          return 0;
        }
        return costo;
      },
      sortComparator: (v1, v2) => {
        if (v1 === -1 && v2 === -1) return 0;
        if (v1 === -1) return 1;
        if (v2 === -1) return -1;
        return v1 - v2;
      },
      renderCell: (params) => {
        const costo = params.value;
        if (costo === undefined || costo === null) {
          return "-";
        }
        return costo;
      },
    },
    {
      field: "rastreo",
      headerName: "Últ. Rastreo",
      width: 250,
      sortable: true,
      valueGetter: (value, row) => {
        const rastreoArray = row.rastreo;
        if (
          !rastreoArray ||
          !Array.isArray(rastreoArray) ||
          rastreoArray.length === 0
        ) {
          return 0; // Put empty rastreo at the beginning (or use a very old date)
        }

        const lastIndex = rastreoArray.length - 1;
        const lastRastreo = rastreoArray[lastIndex];

        if (!lastRastreo || !lastRastreo.fecha || !lastRastreo.hora) {
          return 0;
        }

        // Convert to timestamp for sorting
        try {
          let dateObj: dayjs.Dayjs;
          if (
            typeof lastRastreo.fecha === "string" &&
            typeof lastRastreo.hora === "string"
          ) {
            const dateTimeStr = `${lastRastreo.fecha} ${lastRastreo.hora}`;
            dateObj = dayjs(dateTimeStr);
          } else {
            dateObj = dayjs(lastRastreo.fecha);
          }

          if (!dateObj.isValid()) {
            return 0;
          }

          return dateObj.valueOf(); // Return timestamp
        } catch (error) {
          return 0;
        }
      },
      sortComparator: (v1, v2) => {
        return v1 - v2; // Sort by timestamp
      },
      renderCell: (params) => {
        const rastreoArray = params.row.rastreo;
        if (
          !rastreoArray ||
          !Array.isArray(rastreoArray) ||
          rastreoArray.length === 0
        ) {
          return "-";
        }

        const lastIndex = rastreoArray.length - 1;
        const lastRastreo = rastreoArray[lastIndex];

        if (!lastRastreo || !lastRastreo.fecha || !lastRastreo.hora) {
          return "-";
        }

        return formatFriendlyDate(lastRastreo.fecha, lastRastreo.hora);
      },
    },
];

  return (
    <>
      <ModalNoCredit
        isOpen={isOpenModalNoCredit}
        onClose={() => setIsOpenModalNoCredit(false)}
      />
      <ModalHasNotRates
        isOpen={isOpenModalHasNotRates}
        onClose={() => setIsOpenModalHasNotRates(false)}
      />
      {isOpenModalDeletePackage && (
        <ModalDeletePackage
          isOpen={isOpenModalDeletePackage}
          onClose={() => {
            setPackageForDelete(undefined);
            setIsOpenModalDeletePackage(false);
          }}
          paquete={packageForDelete}
          ids={
            selectionModel?.ids
              ? Array.from(selectionModel.ids).map((id) => String(id))
              : []
          }
        />
      )}
      <ModalFormPackage
        isOpen={isOpen}
        onClose={handleCloseModal}
        entity={entity}
        initialStep={initialStep}
      />
      <ModalPackagesInBatch
        isOpen={isOpenModalInBatch}
        onClose={() => setIsOpenModalInBatch(false)}
        ids={selectionModel?.ids}
      />
      <ModalCreateShipment
        isOpen={isOpenModalCreateShipment}
        onClose={() => setIsOpenModalCreateShipment(false)}
        ids={selectionModel?.ids}
      />
      {packageForTimeline && (
        <ModalTimeline
          isOpen={isOpenModalTimeline}
          onClose={() => {
            setIsOpenModalTimeline(false);
            setPackageForTimeline(undefined);
          }}
          items={getTimelineItemsFromPaquete(packageForTimeline)}
          entityName={packageForTimeline.idRastreo}
          title="Historial de cambios de estado"
        />
      )}
      <Paper sx={{ padding: 2, width: "auto", marginTop: 2 }}>
        {isLoadingAll && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Alert severity="info" sx={{ mt: 1 }}>
              Cargando paquetes... {allPaquetes.length} de {countTotal} cargados
            </Alert>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            jsutifyContent: "space-between",
            marginBottom: 2,
            flexWrap: "nowrap",
            alignItems: "center",
          }}
        >
          {statusFilter && (
            <Chip
              label={`Filtro: ${estados.find((e) => e.id === statusFilter)?.nombre || statusFilter}`}
              onDelete={onClearFilter}
              color="primary"
              sx={{ fontWeight: "bold", flexShrink: 0 }}
            />
          )}
          {envioFilter && (
            <Chip
              label={`Filtro: Envío "${envioFilter}"`}
              onDelete={onClearEnvioFilter}
              color="primary"
              sx={{ fontWeight: "bold", flexShrink: 0 }}
            />
          )}
          <TextField
            label="Buscar paquetes"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1, maxWidth: 400, minWidth: 150 }}
          />
          <Button
            variant="contained"
            startIcon={<AddBox />}
            onClick={() => handleCreate()}
            size="small"
          >
            Nuevo Paquete
          </Button>
          <Button
            size="small"
            disabled={
              !filteredRows.slice(
                paginationModel.page * paginationModel.pageSize,
                (paginationModel.page + 1) * paginationModel.pageSize
              ).length
            }
            onClick={() => {
              const fechaInicioFormateada = fechaInicio.format("DD/MM/YYYY");
              const fechaFinFormateada = fechaFin.format("DD/MM/YYYY");
              const fileName = `${fechaInicioFormateada}-${fechaFinFormateada}.pdf`;
              exportPDF(
                fileName,
                filteredRows,
                agencia?.nombre ?? "",
                fechaInicioFormateada + " a " + fechaFinFormateada
              );
            }}
          >
            <Tooltip title="Exportar como archivo PDF">
              <PictureAsPdf sx={{ width: '1.2em', height: '1.2em' }} />
            </Tooltip>
          </Button>
          <Button
            size="small"
            disabled={
              !filteredRows.slice(
                paginationModel.page * paginationModel.pageSize,
                (paginationModel.page + 1) * paginationModel.pageSize
              ).length
            }
            onClick={() => {
              const fechaInicioFormateada = fechaInicio.format("DD/MM/YYYY");
              const fechaFinFormateada = fechaFin.format("DD/MM/YYYY");
              const fileName = `${fechaInicioFormateada}-${fechaFinFormateada}.xlsx`;
              exportExcel(
                fileName,
                filteredRows,
                agencia?.nombre ?? "",
                fechaInicioFormateada + " a " + fechaFinFormateada
              );
            }}
          >
            <Tooltip title="Exportar como archivo .xlsx">
              <TableView sx={{ width: '1.2em', height: '1.2em' }} />
            </Tooltip>
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 2,
            padding: 1.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Total: {filteredRows.length} paquete(s)
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">Filas por página:</Typography>
              <Select
                value={paginationModel.pageSize === -1 ? filteredRows.length : paginationModel.pageSize}
                onChange={(e) => {
                  const newPageSize = e.target.value === filteredRows.length ? -1 : Number(e.target.value);
                  const newModel = {
                    page: 0,
                    pageSize: newPageSize,
                  };
                  setPaginationModel(newModel);
                  // Sincronizar con DataTable usando el apiRef
                  if (apiRef.current) {
                    apiRef.current.setPaginationModel(newModel);
                  }
                }}
                size="small"
                sx={{ minWidth: 100 }}
              >
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={filteredRows.length}>Todos</MenuItem>
              </Select>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">
                {(() => {
                  const effectivePageSize = paginationModel.pageSize === -1 
                    ? filteredRows.length 
                    : paginationModel.pageSize;
                  const start = paginationModel.page * effectivePageSize + 1;
                  const end = Math.min(
                    (paginationModel.page + 1) * effectivePageSize,
                    filteredRows.length
                  );
                  return `${start}-${end} de ${filteredRows.length}`;
                })()}
              </Typography>
              <IconButton
                onClick={() => {
                  const newModel = { ...paginationModel, page: 0 };
                  setPaginationModel(newModel);
                  if (apiRef.current) {
                    apiRef.current.setPaginationModel(newModel);
                  }
                }}
                disabled={paginationModel.page === 0}
                size="small"
              >
                <FirstPage />
              </IconButton>
              <IconButton
                onClick={() => {
                  const newModel = { ...paginationModel, page: paginationModel.page - 1 };
                  setPaginationModel(newModel);
                  if (apiRef.current) {
                    apiRef.current.setPaginationModel(newModel);
                  }
                }}
                disabled={paginationModel.page === 0}
                size="small"
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                onClick={() => {
                  const newModel = { ...paginationModel, page: paginationModel.page + 1 };
                  setPaginationModel(newModel);
                  if (apiRef.current) {
                    apiRef.current.setPaginationModel(newModel);
                  }
                }}
                disabled={
                  paginationModel.pageSize === -1 ||
                  (paginationModel.page + 1) * paginationModel.pageSize >= filteredRows.length
                }
                size="small"
              >
                <ChevronRight />
              </IconButton>
              <IconButton
                onClick={() => {
                  const effectivePageSize = paginationModel.pageSize === -1 
                    ? filteredRows.length 
                    : paginationModel.pageSize;
                  const lastPage = Math.ceil(filteredRows.length / effectivePageSize) - 1;
                  const newModel = { ...paginationModel, page: lastPage };
                  setPaginationModel(newModel);
                  if (apiRef.current) {
                    apiRef.current.setPaginationModel(newModel);
                  }
                }}
                disabled={
                  paginationModel.pageSize === -1 ||
                  (paginationModel.page + 1) * paginationModel.pageSize >= filteredRows.length
                }
                size="small"
              >
                <LastPage />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {selectionModel?.ids && selectionModel.ids.size > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 2,
              padding: 1.5,
              bgcolor: "action.hover",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {selectionModel.ids.size} elemento(s) seleccionado(s)
            </Typography>
            <Stack direction="row" spacing={1}>
              {(() => {
                const selectedIds = Array.from(selectionModel.ids);
                const selectedPaquetes = allPaquetes.filter((p) => selectedIds.includes(p.id));
                const hasEnvio = selectedPaquetes.some((p) => p.envio);
                const canCreateShipment = !hasEnvio && selectedIds.length > 0;
                
                return (
                  <>
                    {canCreateShipment && (
                      <Button 
                        variant="contained" 
                        size="small" 
                        onClick={() => setIsOpenModalCreateShipment(true)}
                        startIcon={<LocalShipping />}
                        title="Crear envío con los paquetes seleccionados"
                      >
                        Crear envío
                      </Button>
                    )}
                    <Button 
                      variant="outlined" 
                      size="small" 
                      onClick={() => {
                        // Con un solo paquete seleccionado se abre el modal de
                        // edición individual; con más de uno, el de lotes.
                        if (selectedPaquetes.length === 1) {
                          handleEdit(selectedPaquetes[0]);
                          return;
                        }
                        setIsOpenModalInBatch(true);
                      }}
                      startIcon={<Edit />}
                    >
                      Editar seleccionados
                    </Button>
                    {canDelete && (
                      <Button 
                        variant="outlined" 
                        color="error" 
                        size="small" 
                        onClick={() => setIsOpenModalDeletePackage(true)}
                        startIcon={<Delete />}
                      >
                        Eliminar seleccionados
                      </Button>
                    )}
                  </>
                );
              })()}
            </Stack>
          </Box>
        )}

        <DataTable<PaqueteDto>
          apiRef={apiRef}
          disableRowSelectionOnClick={true}
          sortingMode="client"
          onRowDoubleClick={(params) => {
            // Imperatively select row on double click (toggle behavior optional)
            const id = params.id;
            if (!apiRef.current) return;
            const current = apiRef.current.getSelectedRows();
            const isSelected = current.has(id);
            // Decide whether to toggle or force single selection
            if (isSelected) {
              // If you want to keep it selected and also maybe open edit, do nothing to selection
              // Or uncomment below to toggle off
              // apiRef.current.selectRow(id, false);
            } else {
              // Clear previous selection to mimic single-select on double click
              const currentApiRef = apiRef.current;
              if (currentApiRef) {
                current.forEach((_, selectedId) => {
                  currentApiRef.selectRow(selectedId, false);
                });
                currentApiRef.selectRow(id, true);
              }
            }
            // Keep external state in sync
            if (apiRef.current) {
              const updated = apiRef.current.getSelectedRows();
              setSelectionModel({ ids: new Set(updated.keys()) } as any);
            }
            // Abrir el resumen del paquete
            handleView(params.row as PaqueteDto);
          }}
          rows={filteredRows}
          columns={columns}
          onSelectionModelChange={setSelectionModel}
          processRowUpdate={async (newRow: PaqueteDto, oldRow: PaqueteDto) => {
            try {
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { usuario, ...paquete } = newRow;
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { usuario: _usuario, ...oldPaquete } = oldRow;
              
              // Verificar si hay cambios
              const hasChanges = newRow.estado !== oldRow.estado || newRow.envio !== oldRow.envio;

              // No se puede entregar un paquete sin peso ni tarifa.
              const faltantesEntrega = camposFaltantesSegunEstado(
                agencia,
                newRow.estado,
                newRow
              );
              if (newRow.estado !== oldRow.estado && faltantesEntrega.length > 0) {
                toast.error(mensajeEntregaIncompleta(faltantesEntrega));
                return oldRow;
              }

              if (hasChanges) {
                const paqueteParaApi = { ...paquete, estado: newRow.estado, envio: newRow.envio };
                if (paqueteParaApi.tarifa && typeof paqueteParaApi.tarifa === "object") {
                  const { nombre: _nom, ...tarifaRest } = paqueteParaApi.tarifa as unknown as { nombre?: string; [k: string]: unknown };
                  paqueteParaApi.tarifa = tarifaRest as unknown as Paquete["tarifa"];
                }
                await updatePaquete(paqueteParaApi as Paquete, oldPaquete as Paquete);
                updatePaqueteInStore(oldRow.id, newRow);
                toast.success("Paquete actualizado con éxito");
              }
              return newRow;
            } catch (error) {
              console.error("Error al actualizar:", error);
              toast.error(`Error al actualizar el paquete`);
              return oldRow;
            } finally {
              await fetchCounts();
            }
          }}
          onProcessRowUpdateError={(error) => {
            console.error("Error al procesar actualización:", error);
          }}
            slots={{
            footer: () => {
              // Verificar si alguno de los paquetes seleccionados ya tiene envío
              const selectedIds = selectionModel?.ids ? Array.from(selectionModel.ids) : [];
              const selectedPaquetes = allPaquetes.filter((p) => selectedIds.includes(p.id));
              const hasEnvio = selectedPaquetes.some((p) => p.envio);
              const canCreateShipment = !hasEnvio && selectedIds.length > 0;

              return (
                <FooterTable
                  selectedCount={selectionModel?.ids.size || 0}
                  onBatchEdit={() => setIsOpenModalInBatch(true)}
                  onBatchDelete={() => setIsOpenModalDeletePackage(true)}
                  onCreateShipment={() => setIsOpenModalCreateShipment(true)}
                  canCreateShipment={canCreateShipment}
                  canDelete={canDelete}
                />
              );
            },
          }}
          onPaginationModelChange={(newModel: {
            page: number;
            pageSize: number;
          }) => {
            setPaginationModel(newModel);
          }}
          paginationModel={paginationModel}
          rowCount={filteredRows.length}
        />
      </Paper>
    </>
  );
}
